import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useModules } from '@/contexts/ModuleContext';
import ModuleGate from '@/components/ModuleGate';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Search, Loader2, ExternalLink, Trash2, Clock, FileText, LinkIcon, 
  Settings2, Globe, Languages, Calendar, Layers, ChevronDown, ChevronUp,
  FolderOpen, Save, Filter, Sparkles, Building2, Link2
} from 'lucide-react';
import { toast } from 'sonner';

// Component to render summary text with inline clickable links
const SummaryWithLinks = ({ text }) => {
  if (!text) return null;
  
  // Parse the text to find [[title]](url) patterns
  const linkPattern = /\[\[([^\]]+)\]\]\(([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  
  while ((match = linkPattern.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex, match.index)
      });
    }
    // Add the link
    parts.push({
      type: 'link',
      title: match[1],
      url: match[2]
    });
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex)
    });
  }
  
  // If no links found, just render the text
  if (parts.length === 0) {
    return <div className="whitespace-pre-wrap text-sm leading-relaxed">{text}</div>;
  }
  
  return (
    <div className="text-sm leading-relaxed">
      {parts.map((part, idx) => {
        if (part.type === 'link') {
          return (
            <a
              key={idx}
              href={part.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded bg-blue-50 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-500/30 text-xs font-medium transition-colors"
              onClick={(e) => e.stopPropagation()}
              title={`Quelle: ${part.url}`}
            >
              <Link2 className="h-3 w-3" />
              {part.title}
            </a>
          );
        }
        // Render text, preserving line breaks
        return part.content.split('\n').map((line, lineIdx, arr) => (
          <React.Fragment key={`${idx}-${lineIdx}`}>
            {line}
            {lineIdx < arr.length - 1 && <br />}
          </React.Fragment>
        ));
      })}
    </div>
  );
};

// Parameter Labels
const PARAM_LABELS = {
  languages: {
    de: 'Deutsch',
    en: 'Englisch',
    fr: 'Französisch',
    it: 'Italienisch',
    all: 'Alle Sprachen'
  },
  geographic_scopes: {
    local: 'Lokal',
    national: 'National (DACH)',
    international: 'International'
  },
  search_types: {
    general: 'Allgemeine Infos',
    providers: 'Anbieter/Dienstleister',
    market: 'Marktanalyse',
    competitors: 'Wettbewerber'
  },
  time_ranges: {
    week: 'Letzte Woche',
    month: 'Letzter Monat',
    year: 'Letztes Jahr',
    all: 'Unbegrenzt'
  },
  source_types: {
    news: 'News',
    professional: 'Fachportale',
    company: 'Unternehmenswebsites',
    social: 'Soziale Medien',
    all: 'Alle Quellen'
  },
  result_depths: {
    quick: 'Schnellübersicht',
    detailed: 'Detailliert'
  }
};

const AIResearch = ({ user, onLogout }) => {
  // Search state
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Advanced parameters
  const [params, setParams] = useState({
    language: 'de',
    geographic_scope: 'national',
    search_type: 'general',
    time_range: 'all',
    source_type: 'all',
    result_depth: 'quick'
  });
  
  // Results state
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [filterProject, setFilterProject] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');
  
  // Projects for assignment
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  
  // Save to project dialog
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveToProjectId, setSaveToProjectId] = useState('');
  const [saveTitle, setSaveTitle] = useState('');
  const [savingToProject, setSavingToProject] = useState(false);
  const [resultToSave, setResultToSave] = useState(null);
  
  const { checkModuleAccess } = useModules();
  const access = checkModuleAccess('ai_research');

  useEffect(() => {
    if (access.hasAccess) {
      fetchResults();
      fetchProjects();
    }
  }, [access.hasAccess]);

  // Filter results when filter changes
  useEffect(() => {
    let filtered = [...results];
    
    if (filterProject !== 'all') {
      if (filterProject === 'none') {
        filtered = filtered.filter(r => !r.project_id);
      } else {
        filtered = filtered.filter(r => r.project_id === filterProject);
      }
    }
    
    if (searchFilter) {
      const lowerSearch = searchFilter.toLowerCase();
      filtered = filtered.filter(r => 
        r.query.toLowerCase().includes(lowerSearch) ||
        r.title.toLowerCase().includes(lowerSearch) ||
        r.summary.toLowerCase().includes(lowerSearch)
      );
    }
    
    setFilteredResults(filtered);
  }, [results, filterProject, searchFilter]);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/ai-research`);
      setResults(response.data);
    } catch (error) {
      console.error('Failed to fetch results:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setIsSearching(true);
    try {
      const response = await axios.post(`${API}/ai-research`, { 
        query,
        parameters: params
      });
      setResults(prev => [response.data, ...prev]);
      setQuery('');
      toast.success('Recherche abgeschlossen!');
      
      // Open the result dialog automatically
      setSelectedResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Recherche');
    } finally {
      setIsSearching(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/ai-research/${id}`);
      setResults(prev => prev.filter(r => r.id !== id));
      setSelectedResult(null);
      toast.success('Recherche gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const openSaveDialog = (result) => {
    setResultToSave(result);
    setSaveTitle(result.title);
    setSaveToProjectId(result.project_id || '');
    setShowSaveDialog(true);
  };

  const handleSaveToProject = async () => {
    if (!resultToSave || !saveToProjectId) {
      toast.error('Bitte wählen Sie ein Projekt aus');
      return;
    }

    setSavingToProject(true);
    try {
      const response = await axios.put(`${API}/ai-research/${resultToSave.id}/project`, {
        project_id: saveToProjectId,
        title: saveTitle || resultToSave.title
      });
      
      // Update local state
      setResults(prev => prev.map(r => 
        r.id === resultToSave.id 
          ? { ...r, project_id: saveToProjectId, project_name: response.data.project_name, title: saveTitle || r.title }
          : r
      ));
      
      toast.success(`Recherche dem Projekt "${response.data.project_name}" zugeordnet`);
      setShowSaveDialog(false);
      setResultToSave(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingToProject(false);
    }
  };

  const updateParam = (key, value) => {
    setParams(prev => ({ ...prev, [key]: value }));
  };

  const getParamBadges = () => {
    const badges = [];
    if (params.language !== 'de') badges.push({ key: 'language', label: PARAM_LABELS.languages[params.language] });
    if (params.geographic_scope !== 'national') badges.push({ key: 'geo', label: PARAM_LABELS.geographic_scopes[params.geographic_scope] });
    if (params.search_type !== 'general') badges.push({ key: 'type', label: PARAM_LABELS.search_types[params.search_type] });
    if (params.time_range !== 'all') badges.push({ key: 'time', label: PARAM_LABELS.time_ranges[params.time_range] });
    if (params.source_type !== 'all') badges.push({ key: 'source', label: PARAM_LABELS.source_types[params.source_type] });
    if (params.result_depth !== 'quick') badges.push({ key: 'depth', label: PARAM_LABELS.result_depths[params.result_depth] });
    return badges;
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">KI-Recherchetool</h1>
          <p className="text-muted-foreground mt-2">Web-Recherche mit KI-Zusammenfassung und erweiterten Suchparametern</p>
        </div>

        <ModuleGate moduleId="ai_research" showComingSoon={true}>
          {/* Search Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Neue Recherche
              </CardTitle>
              <CardDescription>
                Geben Sie Ihre Suchanfrage ein und passen Sie die Parameter nach Bedarf an.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleSearch} className="space-y-4">
                {/* Main search input */}
                <div className="flex gap-4">
                  <div className="flex-1 relative">
                    <Input
                      placeholder="z.B. Aktuelle Trends im Projektmanagement 2025"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className="pr-10"
                      disabled={isSearching}
                    />
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  </div>
                  <Button type="submit" disabled={isSearching || !query.trim()}>
                    {isSearching ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Recherchiere...</>
                    ) : (
                      <><Search className="h-4 w-4 mr-2" /> Recherchieren</>
                    )}
                  </Button>
                </div>

                {/* Active parameter badges */}
                {getParamBadges().length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {getParamBadges().map(badge => (
                      <Badge key={badge.key} variant="secondary" className="text-xs">
                        {badge.label}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Advanced Parameters Toggle */}
                <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                      <Settings2 className="h-4 w-4" />
                      Erweiterte Parameter
                      {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                      {/* Language */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2 text-sm">
                          <Languages className="h-4 w-4" />
                          Sprachregion
                        </Label>
                        <Select value={params.language} onValueChange={(v) => updateParam('language', v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="de">Deutsch</SelectItem>
                            <SelectItem value="en">Englisch</SelectItem>
                            <SelectItem value="fr">Französisch</SelectItem>
                            <SelectItem value="it">Italienisch</SelectItem>
                            <SelectItem value="all">Alle Sprachen</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Geographic Scope */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2 text-sm">
                          <Globe className="h-4 w-4" />
                          Geografischer Fokus
                        </Label>
                        <Select value={params.geographic_scope} onValueChange={(v) => updateParam('geographic_scope', v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="local">Lokal</SelectItem>
                            <SelectItem value="national">National (DACH)</SelectItem>
                            <SelectItem value="international">International</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Search Type */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2 text-sm">
                          <Layers className="h-4 w-4" />
                          Suchtyp
                        </Label>
                        <Select value={params.search_type} onValueChange={(v) => updateParam('search_type', v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">Allgemeine Infos</SelectItem>
                            <SelectItem value="providers">Anbieter/Dienstleister</SelectItem>
                            <SelectItem value="market">Marktanalyse</SelectItem>
                            <SelectItem value="competitors">Wettbewerber</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Time Range */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4" />
                          Zeitraum
                        </Label>
                        <Select value={params.time_range} onValueChange={(v) => updateParam('time_range', v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="week">Letzte Woche</SelectItem>
                            <SelectItem value="month">Letzter Monat</SelectItem>
                            <SelectItem value="year">Letztes Jahr</SelectItem>
                            <SelectItem value="all">Unbegrenzt</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Source Type */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2 text-sm">
                          <LinkIcon className="h-4 w-4" />
                          Quellen-Typ
                        </Label>
                        <Select value={params.source_type} onValueChange={(v) => updateParam('source_type', v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Alle Quellen</SelectItem>
                            <SelectItem value="news">News</SelectItem>
                            <SelectItem value="professional">Fachportale</SelectItem>
                            <SelectItem value="company">Unternehmenswebsites</SelectItem>
                            <SelectItem value="social">Soziale Medien (LinkedIn, Reddit)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Result Depth */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2 text-sm">
                          <FileText className="h-4 w-4" />
                          Ergebnistiefe
                        </Label>
                        <Select value={params.result_depth} onValueChange={(v) => updateParam('result_depth', v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="quick">Schnellübersicht (3-5 Quellen)</SelectItem>
                            <SelectItem value="detailed">Detailliert (8-10 Quellen)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </form>
            </CardContent>
          </Card>

          {/* Results Section */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Gespeicherte Recherchen</CardTitle>
                  <CardDescription>{filteredResults.length} von {results.length} Recherchen</CardDescription>
                </div>
                
                {/* Filters */}
                <div className="flex flex-wrap gap-2">
                  <div className="relative">
                    <Input
                      placeholder="Suchen..."
                      value={searchFilter}
                      onChange={(e) => setSearchFilter(e.target.value)}
                      className="w-[180px] pl-8"
                    />
                    <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  </div>
                  
                  <Select value={filterProject} onValueChange={setFilterProject}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Alle Projekte" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Projekte</SelectItem>
                      <SelectItem value="none">Ohne Projekt</SelectItem>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredResults.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Keine Recherchen gefunden</p>
                  <p className="text-sm">
                    {results.length > 0 
                      ? 'Passen Sie die Filter an oder starten Sie eine neue Recherche'
                      : 'Starten Sie eine neue Recherche oben'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredResults.map((result) => (
                    <div
                      key={result.id}
                      className="flex items-start justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => setSelectedResult(result)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2">
                          <h4 className="font-medium truncate">{result.title}</h4>
                          {result.project_name && (
                            <Badge variant="outline" className="shrink-0 text-xs">
                              <Building2 className="h-3 w-3 mr-1" />
                              {result.project_name}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                          {result.summary.substring(0, 150)}...
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(result.created_at).toLocaleDateString('de-CH')}
                          </span>
                          <span className="flex items-center gap-1">
                            <LinkIcon className="h-3 w-3" />
                            {result.sources?.length || 0} Quellen
                          </span>
                          {result.parameters && (
                            <>
                              <Badge variant="secondary" className="text-xs py-0">
                                {PARAM_LABELS.search_types[result.parameters.search_type] || 'Allgemein'}
                              </Badge>
                              <Badge variant="secondary" className="text-xs py-0">
                                {PARAM_LABELS.languages[result.parameters.language] || 'DE'}
                              </Badge>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            openSaveDialog(result);
                          }}
                          title="Projekt zuordnen"
                        >
                          <FolderOpen className="h-4 w-4 text-muted-foreground hover:text-primary" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(result.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </ModuleGate>

        {/* Detail Dialog */}
        <Dialog open={!!selectedResult} onOpenChange={() => setSelectedResult(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <DialogTitle className="flex items-center gap-2 text-lg">
                    <Sparkles className="h-5 w-5 text-primary" />
                    {selectedResult?.title}
                  </DialogTitle>
                  <DialogDescription className="mt-1">
                    <span className="flex items-center gap-2">
                      Recherchiert am {selectedResult && new Date(selectedResult.created_at).toLocaleString('de-CH')}
                      {selectedResult?.project_name && (
                        <Badge variant="outline" className="ml-2">
                          <Building2 className="h-3 w-3 mr-1" />
                          {selectedResult.project_name}
                        </Badge>
                      )}
                    </span>
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Search Query */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <Label className="text-xs text-muted-foreground">Suchanfrage</Label>
                <p className="font-medium mt-1">{selectedResult?.query}</p>
              </div>

              {/* Parameters used */}
              {selectedResult?.parameters && (
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="text-xs">
                    <Languages className="h-3 w-3 mr-1" />
                    {PARAM_LABELS.languages[selectedResult.parameters.language]}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    <Globe className="h-3 w-3 mr-1" />
                    {PARAM_LABELS.geographic_scopes[selectedResult.parameters.geographic_scope]}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    <Layers className="h-3 w-3 mr-1" />
                    {PARAM_LABELS.search_types[selectedResult.parameters.search_type]}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    <Calendar className="h-3 w-3 mr-1" />
                    {PARAM_LABELS.time_ranges[selectedResult.parameters.time_range]}
                  </Badge>
                </div>
              )}

              {/* Summary with inline links */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Zusammenfassung
                </h4>
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <SummaryWithLinks text={selectedResult?.summary} />
                </div>
              </div>
              
              {/* Sources - Grouped by type */}
              {selectedResult?.sources && selectedResult.sources.length > 0 && (
                <div className="space-y-4">
                  {/* Publications & Magazines */}
                  {selectedResult.sources.filter(s => s.url && s.type === 'publication').length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                        <FileText className="h-4 w-4 text-blue-500" />
                        Fachmagazine & Portale
                      </h4>
                      <div className="space-y-1.5">
                        {selectedResult.sources
                          .filter(source => source.url && source.type === 'publication')
                          .map((source, idx) => (
                          <a
                            key={idx}
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-2 border rounded-lg hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors group"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                            <span className="font-medium text-sm group-hover:text-blue-600 dark:group-hover:text-blue-400">{source.title}</span>
                            <span className="text-xs text-muted-foreground">— {source.snippet}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Studies & Research */}
                  {selectedResult.sources.filter(s => s.url && s.type === 'study').length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                        <Layers className="h-4 w-4 text-purple-500" />
                        Studien & Research
                      </h4>
                      <div className="space-y-1.5">
                        {selectedResult.sources
                          .filter(source => source.url && source.type === 'study')
                          .map((source, idx) => (
                          <a
                            key={idx}
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-2 border rounded-lg hover:bg-purple-50 dark:hover:bg-purple-500/10 transition-colors group"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-3.5 w-3.5 text-purple-500 shrink-0" />
                            <span className="font-medium text-sm group-hover:text-purple-600 dark:group-hover:text-purple-400">{source.title}</span>
                            <span className="text-xs text-muted-foreground">— {source.snippet}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Software Tools */}
                  {selectedResult.sources.filter(s => s.url && s.type === 'tool').length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                        <Building2 className="h-4 w-4 text-green-500" />
                        Software & Tools
                      </h4>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedResult.sources
                          .filter(source => source.url && source.type === 'tool')
                          .map((source, idx) => (
                          <a
                            key={idx}
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 border rounded-full text-xs hover:bg-green-50 dark:hover:bg-green-500/10 transition-colors group"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="font-medium group-hover:text-green-600 dark:group-hover:text-green-400">{source.title}</span>
                            <ExternalLink className="h-3 w-3 text-green-500" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Fallback for sources without type */}
                  {selectedResult.sources.filter(s => s.url && s.url.startsWith('http') && !s.type).length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                        <LinkIcon className="h-4 w-4" />
                        Weitere Quellen
                      </h4>
                      <div className="space-y-1.5">
                        {selectedResult.sources
                          .filter(source => source.url && source.url.startsWith('http') && !source.type)
                          .map((source, idx) => (
                          <a
                            key={idx}
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-2 border rounded-lg hover:bg-muted/50 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span className="font-medium text-sm">{source.title}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedResult.sources.filter(s => s.url && s.url.startsWith('http')).length === 0 && (
                    <p className="text-sm text-muted-foreground">Keine externen Quellen in dieser Recherche gefunden.</p>
                  )}
                </div>
              )}
            </div>
            
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button 
                variant="outline" 
                onClick={() => openSaveDialog(selectedResult)}
                className="w-full sm:w-auto"
              >
                <FolderOpen className="h-4 w-4 mr-2" />
                Projekt zuordnen
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setSelectedResult(null)}
                className="w-full sm:w-auto"
              >
                Schliessen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Save to Project Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Save className="h-5 w-5" />
                Recherche einem Projekt zuordnen
              </DialogTitle>
              <DialogDescription>
                Ordnen Sie diese Recherche einem Projekt zu, um sie später wiederzufinden.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Titel</Label>
                <Input
                  value={saveTitle}
                  onChange={(e) => setSaveTitle(e.target.value)}
                  placeholder="Recherche-Titel"
                />
              </div>
              <div className="space-y-2">
                <Label>Projekt auswählen *</Label>
                <Select value={saveToProjectId} onValueChange={setSaveToProjectId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Projekt auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        <span className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          {p.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={handleSaveToProject} 
                disabled={savingToProject || !saveToProjectId}
              >
                {savingToProject ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Speichern...</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" /> Speichern</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AIResearch;
